<script setup>
import { computed } from 'vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import Tooltip from '@/components/Atoms/Tooltip.vue'
import DropDownMenuItem from '@/components/Atoms/DropDownMenuItem.vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconClock from '@/components/icon/IconClock.vue'
import IconEyeCrossed from '@/components/icon/IconEyeCrossed.vue'
import MediaflowDock from './MediaflowDock.vue'
import MediaflowMenu from './MediaflowMenu.vue'
import MediaflowFlowThumb from './MediaflowFlowThumb.vue'
import MediaflowDockEmpty from './MediaflowDockEmpty.vue'
import MediaflowSceneLimit from './MediaflowSceneLimit.vue'
import MediaflowSourcesTip from './MediaflowSourcesTip.vue'
import {
	cueRemainingSec, formatCountdown, formatDurationShort, ENGINE_HINT, DOCK_DANGER_ITEM,
	sceneLimitReached, sceneLimitReason,
} from './mediaflowMeta'

/**
 * Breaks dock: ad pods and L-bands (flows of kind 'break'). "Cue now" puts
 * one on Program and the server brings the program back when its time is up —
 * the countdown comes from the production's `cue`, not this browser.
 */
const props = defineProps({
	desk: { type: Object, required: true },
	canvas: { type: Object, required: true },
	enabled: { type: Boolean, default: true },
	sessionActive: { type: Boolean, default: false },
	now: { type: Number, default: () => Date.now() },
	/** Plan scene limit; scenes and breaks count toward it together. */
	maxFlows: { type: Number, default: 0 },
	flowCount: { type: Number, default: 0 },
	mobile: { type: Boolean, default: false },
	readOnly: { type: Boolean, default: false },
	/** Set when the plan's schedule limit is reached: "Schedule…" is off and says why. */
	scheduleDisabledReason: { type: String, default: '' },
})

const emit = defineEmits(['cue', 'end', 'settings', 'schedule', 'edit-layout', 'rename', 'delete', 'add'])


const breaks = computed(() => props.desk.breaks.value)
const layouts = computed(() => props.desk.layouts.value)
const cue = computed(() => props.desk.cue.value)
const capable = computed(() => props.desk.capabilities.value.cueBreaks)
const cueing = computed(() => props.desk.cueing.value)
const remaining = computed(() => cueRemainingSec(cue.value, props.now))
const running = (flow) => !!cue.value && cue.value.flowId === flow._id

const atLimit = computed(() => sceneLimitReached(props.flowCount, props.maxFlows))
const addReason = computed(() => {
	if (!props.enabled) return 'Enable the production first'
	if (atLimit.value) return sceneLimitReason(props.flowCount, props.maxFlows)
	return ''
})
const cueReason = (flow) => {
	if (!props.enabled) return 'Enable the production first'
	if (!capable.value) return ENGINE_HINT
	if (!props.sessionActive) return 'Go live first'
	if (cue.value && !running(flow)) return 'Another break is running. End it first.'
	return ''
}
const sourceCount = (flow) => (Array.isArray(flow.media) ? flow.media.length : 0)
const sourceLabel = (flow) => {
	const n = sourceCount(flow)
	return n === 1 ? '1 source' : `${n} sources`
}
// One click shows the break on Preview (never on air: only "Cue now" airs it).
const onRowClick = (flow, event) => {
	if (props.mobile) return
	if (event?.target?.closest?.('button:not([data-row-select]), a, input, select, [role="menu"], [role="menuitem"]')) return
	emit('edit-layout', flow)
}
/** The break as Preview shows it (unapplied edits included). */
const draftedOf = (flow) => props.desk.draftedFlows?.value?.find((f) => f._id === flow._id) || flow
const returnName = (flow) => {
	const id = flow.break?.returnFlowId
	return id ? layouts.value.find((f) => f._id === id)?.name || '' : ''
}
</script>

<template>
	<MediaflowDock
		title="Breaks"
		:count="String(breaks.length)"
		testid="dock-breaks"
		:locked="!capable"
		:locked-reason="ENGINE_HINT"
		:add-label="readOnly ? '' : 'New break'"
		:add-disabled-reason="addReason"
		@add="emit('add')"
	>
		<template v-if="maxFlows > 0 && !readOnly" #header-extra>
			<MediaflowSceneLimit :used="flowCount" :max="maxFlows" :reached="atLimit" testid="dock-breaks-usage" />
		</template>
		<template #context>Breaks count as scenes</template>
		<template v-if="atLimit && !readOnly" #footer>
			<MediaflowSceneLimit variant="notice" :used="flowCount" :max="maxFlows" reached testid="dock-breaks-limit" />
		</template>

		<MediaflowDockEmpty v-if="!breaks.length" text="No breaks yet." :action-label="readOnly || atLimit ? '' : 'Add break'" :disabled-reason="readOnly || atLimit ? '' : addReason" @add="emit('add')" />
		<ul v-else class="m-0 flex list-none flex-col gap-1.5 p-0">
			<li
				v-for="flow in breaks"
				:key="flow._id"
				class="mf-row flex items-center gap-1.5 rounded-lg p-1.5 pr-0"
				:class="[running(flow) ? 'bg-error-transparent ring-1 ring-error' : flow._id === desk.editingFlowId.value && !mobile ? 'bg-success-transparent ring-1 ring-success' : '', mobile ? '' : 'cursor-pointer']"
				:data-tone="running(flow) ? 'live' : flow._id === desk.editingFlowId.value && !mobile ? 'preview' : undefined"
				data-testid="break-row"
				:data-flow-id="flow._id"
				:data-live="running(flow) ? 'true' : undefined"
				:data-preview="flow._id === desk.editingFlowId.value && !mobile ? 'true' : undefined"
				@click="onRowClick(flow, $event)"
			>
				<span class="hidden w-11 shrink-0 self-start overflow-hidden rounded ring-1 ring-surface-4 2xl:block"><MediaflowFlowThumb :flow="flow" :canvas="canvas" /></span>
				<span class="flex min-w-0 flex-1 flex-col gap-1 leading-tight">
					<MediaflowSourcesTip :flow="flow" :drafted="draftedOf(flow)" hint="Click: show in Preview · Cue now puts it on air" :disabled="mobile" wrapper-class="min-w-0">
					<span class="flex min-w-0 items-baseline gap-1.5">
						<button
							v-if="!mobile"
							type="button"
							class="min-w-0 truncate rounded text-left text-[0.8rem] font-medium text-surface-text-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-main"
							:aria-label="`Show ${flow.name} in Preview`"
							data-row-select
							data-testid="break-select"
						>{{ flow.name }}</button>
						<span v-else class="truncate text-[0.8rem] font-medium text-surface-text-2" :title="flow.name">{{ flow.name }}</span>
						<span v-if="sourceCount(flow)" class="shrink-0 text-[0.7rem] text-surface-7" data-testid="break-sources">{{ sourceLabel(flow) }}</span>
						<span v-else class="shrink-0 text-[0.7rem] font-medium text-gold" title="Viewers see black while it runs" data-testid="break-sources">Empty · add ad media</span>
					</span>
					</MediaflowSourcesTip>
					<span class="flex min-w-0 items-center gap-1.5 text-[0.7rem] text-surface-8">
						<template v-if="running(flow)">
							<span class="shrink-0 rounded bg-error px-1 py-px text-[0.58rem] font-semibold tracking-wider text-white">ON AIR</span>
							<span class="font-mono text-[0.8rem] font-semibold tabular-nums text-white" data-testid="break-countdown">{{ remaining !== null ? formatCountdown(remaining) : '--:--' }}</span>
						</template>
						<template v-else>
							<span class="inline-flex shrink-0 items-center gap-1 tabular-nums" data-testid="break-duration">
								<icon-base width="10" height="10"><icon-clock /></icon-base>{{ formatDurationShort(flow.break?.durationSec || 30) }}
							</span>
							<Tooltip v-if="flow.break?.overlays === 'hide'" content="Hides graphics while it runs" wrapper-class="inline-flex">
								<span class="inline-flex shrink-0 items-center text-surface-7" aria-label="Hides graphics while it runs" data-testid="break-hides-graphics">
									<icon-base width="11" height="11" fill="none"><icon-eye-crossed /></icon-base>
								</span>
							</Tooltip>
							<span class="min-w-0 truncate" :title="`Then back to ${returnName(flow) || 'the previous scene'}`">→ {{ returnName(flow) || 'previous' }}</span>
						</template>
						<span class="ml-auto shrink-0 pl-1">
							<FormButton v-if="running(flow)" type="red" size="xs" class="!px-2 font-semibold" :is-loading="cueing" data-testid="break-end" @click="emit('end')">End now</FormButton>
							<Tooltip v-else :active="!!cueReason(flow)" :content="cueReason(flow)" wrapper-class="inline-flex" wrap-trigger popper-class="max-w-[240px] text-sm break-words">
								<FormButton type="secondary" size="xs" class="!px-2 font-semibold" :disabled="!!cueReason(flow) || cueing" data-testid="break-cue" @click="emit('cue', flow)">Cue now</FormButton>
							</Tooltip>
						</span>
					</span>
				</span>
				<MediaflowMenu v-if="!readOnly || !mobile" :aria-label="`Actions for ${flow.name}`" list-classes="min-w-[200px]">
					<template v-if="!readOnly">
						<DropDownMenuItem :disabled="!enabled" @click="emit('settings', flow)">Break settings…</DropDownMenuItem>
						<DropDownMenuItem :disabled="!enabled || !!scheduleDisabledReason" :title="scheduleDisabledReason || undefined" data-testid="break-schedule" @click="emit('schedule', flow)">Schedule…</DropDownMenuItem>
					</template>
					<DropDownMenuItem v-if="!mobile" :disabled="!enabled" @click="emit('edit-layout', flow)">Edit layout on Preview</DropDownMenuItem>
					<template v-if="!readOnly">
						<DropDownMenuItem :disabled="!enabled" @click="emit('rename', flow)">Rename</DropDownMenuItem>
						<DropDownMenuItem :disabled="!enabled || running(flow)" :custom-classes="DOCK_DANGER_ITEM" @click="emit('delete', flow)">Delete</DropDownMenuItem>
					</template>
				</MediaflowMenu>
			</li>
		</ul>
	</MediaflowDock>
</template>
